import { useState, useEffect, useMemo } from 'react'
import { useUserStore } from 'Store/User'
import { useChartStore } from 'Store/chart'
import { shallow } from 'zustand/shallow'

const INITIAL_FILTER_STATE = {
  machineType: 'Pure Drive',
}

const ViewModel = () => {
  const { projectProgressionByMachine, getProjectProgressionByMachine } = useChartStore(
    state => ({
      projectProgressionByMachine: state.projectProgressionByMachine,
      getProjectProgressionByMachine: state.getProjectProgressionByMachine,
    }),
    shallow
  )
  const { rawSales, getSales } = useUserStore(
    state => ({
      rawSales: state.data || [],
      getSales: state.getAll,
    }),
    shallow
  )

  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE)

  const InstallationTypes = ['Pure Drive', 'Prebored + Drive', 'Auger + Drive', 'FC-PTG', 'JIP']

  const MACHINE_TYPE_FILTERS_OPTIONS = InstallationTypes.map(type => ({
    value: type,
    label: type,
  }))

  const sales = rawSales
    .filter(u => u.roleId === 7)
    .map(s => ({
      label: s.fullName,
      value: s.id,
    }))

  const fetchMasterData = () => {
    getSales()
  }

  const fetchData = () => {
    getProjectProgressionByMachine({
      // machineType: filterState.machineType,
      machineType: 'PD',
    })
  }

  const onChangeFilterState = (key: string, value: any) => {
    setFilterState(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  useEffect(() => {
    fetchMasterData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState])

  const apexChart = useMemo(() => {
    if (!projectProgressionByMachine?.data) {
      return {
        options: {},
        series: [],
      }
    }

    const rows = projectProgressionByMachine.data

    // X-axis labels (project titles)
    const categories = rows.map((r: any) => r.division)

    const series = [
      {
        name: 'Progress (%)',
        data: rows.map((r: any) => Number((r.progressPercent ?? 0).toFixed(1))),
      },
      {
        name: 'Time Used (%)',
        data: rows.map((r: any) => Number((r.timeUsedPercent ?? 0).toFixed(1))),
      },
    ]

    const options = {
      chart: {
        type: 'bar',
        height: 700,
        toolbar: { show: false },
        parentHeightOffset: 30,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
        },
      },
      colors: ['#1E88E5', '#FF7043'],
      dataLabels: {
        enabled: false,
        formatter: (val: any) => `${val}%`,
        style: { fontSize: '12px', fontWeight: 600 },
      },

      xaxis: {
        categories,
        labels: {
          rotate: -35,
          trim: false,
          maxHeight: 300,
          style: {
            fontSize: '8px',
            fontWeight: 500,
            whiteSpace: 'normal',
            lineHeight: '14px',
          },
          formatter: function (val: string) {
            return val.length > 20 ? val.slice(0, 20) + '...' : val
          },
        },
      },

      yaxis: {
        title: {
          text: 'เปอร์เซ็นต์ (%)',
          style: { fontSize: '16px', fontWeight: 700 },
        },
        max: 110,
      },

      legend: {
        position: 'right',
        markers: { radius: 5 },
      },

      // -------------------------------
      //  CUSTOM TOOLTIP (UPDATED)
      // -------------------------------
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex }: any) {
          const r = rows[dataPointIndex]

          if (seriesIndex === 0) {
            // BLUE — Progress
            return `
            <div style="
              background:#0D47A1;
              color:#fff;
              padding:10px;
              border-radius:6px;
              max-width:260px;
              font-size:13px;">
              <b>หมายเลขเครื่องจักร: ${r.machineCode ?? '-'}</b><br/>
              ระบบติดตั้ง: ${r.pillingType ?? '-'}<br/>
              ตอกแล้ว: ${r.totalHammered} / ${r.totalOrderPiles} ต้น<br/>
              ${(r.progressPercent ?? 0).toFixed(1)}% ความคืบหน้า
            </div>`
          } else {
            // ORANGE — Time Used
            return `
            <div style="
              background:#FF6F00;
              color:#fff;
              padding:10px;
              border-radius:6px;
              max-width:260px;
              font-size:13px;">
              <b>หมายเลขเครื่องจักร: ${r.machineCode ?? '-'}</b><br/>
              ระบบติดตั้ง: ${r.pillingType ?? '-'}<br/>
              ใช้เวลาไปแล้ว: ${r.daysUsed} วัน<br/>
              ${(r.timeUsedPercent ?? 0).toFixed(1)}% เวลาใช้
            </div>`
          }
        },
      },
    }

    return { options, series }
  }, [projectProgressionByMachine])

  return {
    sales,
    MACHINE_TYPE_FILTERS_OPTIONS,
    filterState,
    onChangeFilterState,

    // expose chart options to View
    apexChartOptions: apexChart.options,
    apexChartSeries: apexChart.series,
  }
}

export default ViewModel
